import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { readResponse, readResponseJSON } from '../../utils/fetchSafe';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Pretty-prints a backend-stamped ISO ts ("2026-05-20T14:23:01.000+00:00")
// into the user's local "5/20/2026 10:23 am" format.
const fmtTs = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const date = `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }).toLowerCase();
  return `${date} ${time}`;
};

const ProjectHistoryTab = ({ projectId }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!projectId) return;
    const fetchHistory = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/api/projects/${encodeURIComponent(projectId)}/history`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          if (res.status === 403) {
            toast.error('History is admin-only.');
            return;
          }
          throw new Error(`HTTP ${res.status}`);
        }
        const { data } = await readResponse(res);
        setEntries(data.entries || []);
      } catch (e) {
        toast.error(`Failed to load history: ${e.message}`);
      } finally {
        setLoading(false);
      }
    };
    fetchHistory();
  }, [projectId]);

  return (
    <div className="bg-white rounded shadow p-6" data-testid="project-history-tab">
      <h2 className="text-xl text-[#5B8DB8] font-light mb-4">Project History</h2>
      <p className="text-sm text-gray-600 mb-4">
        Append-only audit log of user actions on this project. Visible to admins only.
      </p>
      {loading ? (
        <div className="text-sm text-gray-500 py-6">Loading…</div>
      ) : entries.length === 0 ? (
        <div className="text-sm text-gray-500 py-6">No history recorded yet.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="project-history-table">
            <thead className="border-b-2 border-gray-200 text-gray-600">
              <tr>
                <th className="text-left p-2 font-normal w-44">When</th>
                <th className="text-left p-2 font-normal w-40">User</th>
                <th className="text-left p-2 font-normal w-44">Action</th>
                <th className="text-left p-2 font-normal">Details</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e, i) => (
                <tr key={i} className="border-b hover:bg-gray-50 align-top" data-testid={`project-history-row-${i}`}>
                  <td className="p-2 text-gray-700 whitespace-nowrap">{fmtTs(e.ts)}</td>
                  <td className="p-2 text-gray-700 whitespace-nowrap">{e.user || '—'}</td>
                  <td className="p-2 text-gray-700">{e.action || '—'}</td>
                  <td className="p-2 text-gray-600">
                    {Array.isArray(e.changes) && e.changes.length > 0 ? (
                      <ul className="space-y-1" data-testid={`project-history-changes-${i}`}>
                        {e.changes.map((c, j) => (
                          <li key={j} className="text-gray-700 text-[13px] leading-snug">{c}</li>
                        ))}
                      </ul>
                    ) : (
                      e.details || ''
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ProjectHistoryTab;
